import React, { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { BASE_URL, URL } from "../../redux/constants";
import { setCartItem } from "../../redux/features/cartSlice";
import {
  useAiRematchBomMutation,
  useBuildBomCartItemsMutation,
  useCreateBomItemMutation,
  useDeleteBomItemsMutation,
  useRematchBomMutation,
  useReviewBomAiSuggestionMutation,
  useUpdateBomItemMutation,
  useUploadBomMutation,
} from "../../redux/api/bomApi";
import UniverBomSheet, {
  BOM_SHEET_COLUMNS,
  MATCH_STATUS,
  STATUS_LABELS,
  extractBomSheetRows,
  formatMoney,
  getSelectedBomItemIds,
} from "./UniverBomSheet";
import "./BomOrderPage.css";

/**
 * 获取 BOM 本地会话 ID，游客上传时用于访问自己的 BOM 任务。
 * @returns {string} BOM 会话 ID
 */
const getBomSessionId = () => {
  const storageKey = "bomOrderSessionId";
  const existingId = window.localStorage.getItem(storageKey);
  if (existingId) return existingId;

  const nextId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  window.localStorage.setItem(storageKey, nextId);
  return nextId;
};

/**
 * 判断 BOM 明细是否允许加入购物车。
 * @param {Object} item BOM 明细
 * @returns {boolean} 是否允许选择
 */
const canSelectBomItem = (item = {}) => {
  return item.matchStatus === "matched" && item.selectedProduct && !item.needQuote;
};

/**
 * 计算 BOM 完全匹配率。
 * @param {Object} summary BOM 汇总信息
 * @returns {number} 匹配率
 */
const getMatchRate = (summary = {}) => {
  const totalRows = Number(summary.totalRows || 0);
  if (!totalRows) return 0;
  return Math.round((Number(summary.matchedRows || 0) / totalRows) * 1000) / 10;
};

/**
 * 获取标准 BOM 模板下载地址。
 * @returns {string} 模板接口地址
 */
const getBomTemplateUrl = () => `${BASE_URL || ""}${URL}/bom/template`;

const BOM_COLUMN_PREFERENCES_KEY = "bomOrderColumnPreferences";
const MAX_AI_REMATCH_ROWS = 20;

/**
 * 读取并修复 BOM 云表列偏好，兼容后续新增列。
 * @returns {{order: string[], hiddenKeys: string[]}} 列顺序和隐藏列
 */
const loadBomColumnPreferences = () => {
  const defaultOrder = BOM_SHEET_COLUMNS.map((column) => column.key);
  if (typeof window === "undefined") return { order: defaultOrder, hiddenKeys: [] };

  try {
    const saved = JSON.parse(window.localStorage.getItem(BOM_COLUMN_PREFERENCES_KEY) || "{}");
    const validKeys = new Set(defaultOrder);
    const savedOrder = Array.isArray(saved.order) ? saved.order : [];
    const order = Array.from(new Set(savedOrder.filter((key) => validKeys.has(key))));
    defaultOrder.forEach((key) => {
      if (!order.includes(key)) order.push(key);
    });
    const hiddenKeys = Array.isArray(saved.hiddenKeys)
      ? Array.from(new Set(saved.hiddenKeys.filter((key) => validKeys.has(key))))
      : [];

    return {
      order,
      hiddenKeys: hiddenKeys.length === defaultOrder.length ? [] : hiddenKeys,
    };
  } catch (error) {
    return { order: defaultOrder, hiddenKeys: [] };
  }
};

/**
 * 将 BOM 云表列偏好保存到浏览器本地。
 * @param {{order: string[], hiddenKeys: string[]}} preferences 列偏好
 */
const saveBomColumnPreferences = (preferences) => {
  window.localStorage.setItem(BOM_COLUMN_PREFERENCES_KEY, JSON.stringify(preferences));
};

const BomOrderPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const univerApiRef = useRef(null);
  const [sessionId] = useState(getBomSessionId);
  const [bomOrder, setBomOrder] = useState(null);
  const [activeStatuses, setActiveStatuses] = useState(new Set(MATCH_STATUS));
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [purchaseSets, setPurchaseSets] = useState(1);
  const [onlySelected, setOnlySelected] = useState(false);
  const [columnPreferences, setColumnPreferences] = useState(loadBomColumnPreferences);
  const [columnDraft, setColumnDraft] = useState(null);
  const [draggedColumnKey, setDraggedColumnKey] = useState("");
  const [uploadBom, { isLoading: isUploading }] = useUploadBomMutation();
  const [createBomItem, { isLoading: isCreatingItem }] = useCreateBomItemMutation();
  const [deleteBomItems, { isLoading: isDeletingItems }] = useDeleteBomItemsMutation();
  const [updateBomItem] = useUpdateBomItemMutation();
  const [rematchBom, { isLoading: isRematching }] = useRematchBomMutation();
  const [aiRematchBom, { isLoading: isAiRematching }] = useAiRematchBomMutation();
  const [reviewBomAiSuggestion, { isLoading: isReviewingAi }] = useReviewBomAiSuggestionMutation();
  const [buildBomCartItems, { isLoading: isAdding }] = useBuildBomCartItemsMutation();

  const summary = bomOrder?.summary || {};
  const matchRate = getMatchRate(summary);
  const aiPendingItems = useMemo(
    () => (bomOrder?.items || []).filter((item) => item.matchSource === "ai" && item.requiresConfirmation),
    [bomOrder?.items],
  );
  const visibleColumns = useMemo(() => {
    const columnMap = new Map(BOM_SHEET_COLUMNS.map((column) => [column.key, column]));
    const hiddenKeys = new Set(columnPreferences.hiddenKeys);
    return columnPreferences.order
      .map((key) => columnMap.get(key))
      .filter((column) => column && !hiddenKeys.has(column.key));
  }, [columnPreferences]);

  const filteredItems = useMemo(() => {
    const items = bomOrder?.items || [];
    return items.filter((item) => {
      const statusMatched = activeStatuses.has(item.matchStatus);
      const selectedMatched = !onlySelected || selectedItemIds.has(item._id);
      return statusMatched && selectedMatched;
    });
  }, [activeStatuses, bomOrder?.items, onlySelected, selectedItemIds]);

  /**
   * 用后端返回的 BOM 任务刷新当前表格和默认选中项。
   * @param {Object|null} nextBomOrder BOM 任务
   */
  const refreshBomOrder = (nextBomOrder) => {
    setBomOrder(nextBomOrder || null);
    setSelectedItemIds(
      new Set((nextBomOrder?.items || []).filter((item) => item.selected).map((item) => item._id))
    );
  };

  /**
   * 记录 Univer API，供保存表格编辑时读取当前工作簿。
   * @param {Object|null} univerAPI Univer facade API
   */
  const handleUniverReady = useCallback((univerAPI) => {
    univerApiRef.current = univerAPI;
  }, []);

  /**
   * 上传 BOM 文件并进入在线表格。
   * @param {File} file BOM 文件
   */
  const handleUploadFile = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("sessionId", sessionId);

    try {
      const response = await uploadBom(formData).unwrap();
      refreshBomOrder(response?.bomOrder || null);
      toast.success("BOM online spreadsheet generated");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to upload BOM");
    }
  };

  /**
   * 处理文件输入框变化。
   * @param {Event} event 输入事件
   */
  const handleFileChange = (event) => {
    handleUploadFile(event.target.files?.[0]);
    event.target.value = "";
  };

  /**
   * 触发选择 BOM 文件。
   */
  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  /**
   * 切换匹配状态筛选。
   * @param {string} status 匹配状态
   */
  const toggleStatus = (status) => {
    const nextStatuses = new Set(activeStatuses);
    if (nextStatuses.has(status)) {
      nextStatuses.delete(status);
    } else {
      nextStatuses.add(status);
    }
    setActiveStatuses(nextStatuses);
  };

  /**
   * 切换全部状态筛选。
   * @param {boolean} checked 是否全选
   */
  const toggleAllStatus = (checked) => {
    setActiveStatuses(new Set(checked ? MATCH_STATUS : []));
  };

  /**
   * 切换当前筛选范围内可加购明细的全选状态。
   * @param {boolean} checked 是否全选
   */
  const toggleVisibleSelection = async (checked) => {
    const selectableItems = filteredItems.filter(canSelectBomItem);
    const nextIds = new Set(selectedItemIds);
    selectableItems.forEach((item) => {
      if (checked) nextIds.add(item._id);
      else nextIds.delete(item._id);
    });
    setSelectedItemIds(nextIds);

    try {
      await Promise.all(
        selectableItems.map((item) =>
          updateBomItem({
            bomId: bomOrder._id,
            itemId: item._id,
            sessionId,
            payload: { selected: checked },
          }).unwrap()
        )
      );
    } catch (error) {
      toast.error(error?.data?.message || "Failed to save bulk selection");
    }
  };

  /**
   * 按采购套数批量刷新购买数量。
   */
  const applyPurchaseSets = async () => {
    if (!bomOrder?._id) return;
    const safeSets = Math.max(1, Number(purchaseSets || 1));

    try {
      const responses = await Promise.all(
        (bomOrder.items || []).map((item) => {
          const unitQty = Number(item.normalized?.unitQty || item.original?.unitQty || 1);
          const spareQty = Number(item.normalized?.productionSurplusQty || 0);
          const quantity = unitQty * safeSets + spareQty;

          return updateBomItem({
            bomId: bomOrder._id,
            itemId: item._id,
            sessionId,
            payload: {
              quantity,
              normalized: {
                orderQty: quantity,
              },
            },
          }).unwrap();
        })
      );
      refreshBomOrder(responses[responses.length - 1]?.bomOrder || bomOrder);
      toast.success("Purchase sets applied");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to apply purchase sets");
    }
  };

  /**
   * 保存 Univer 表格中的可编辑 BOM 字段。
   * @param {Object} options 保存选项
   * @param {boolean} options.silent 是否静默保存
   * @returns {Promise<boolean>} 是否保存成功
   */
  const saveSheetEdits = async ({ silent = false } = {}) => {
    if (!bomOrder?._id || !univerApiRef.current) return true;

    const patches = extractBomSheetRows(univerApiRef.current, filteredItems, visibleColumns);
    if (patches.length === 0) return true;

    try {
      const responses = await Promise.all(
        patches.map((patch) =>
          updateBomItem({
            bomId: bomOrder._id,
            itemId: patch.itemId,
            sessionId,
            payload: {
              selected: patch.selected,
              normalized: patch.normalized,
            },
          }).unwrap()
        )
      );
      refreshBomOrder(responses[responses.length - 1]?.bomOrder || bomOrder);
      if (!silent) toast.success("Spreadsheet changes saved");
      return true;
    } catch (error) {
      toast.error(error?.data?.message || "Failed to save spreadsheet changes");
      return false;
    }
  };

  /**
   * 打开自定义列面板并创建一份待应用配置。
   */
  const openColumnSettings = () => {
    setColumnDraft({
      order: [...columnPreferences.order],
      hiddenKeys: [...columnPreferences.hiddenKeys],
    });
  };

  /**
   * 切换草稿中指定列的显示状态，至少保留一列。
   * @param {string} columnKey 列标识
   */
  const toggleDraftColumn = (columnKey) => {
    setColumnDraft((draft) => {
      if (!draft) return draft;
      const hiddenKeys = new Set(draft.hiddenKeys);
      if (hiddenKeys.has(columnKey)) {
        hiddenKeys.delete(columnKey);
      } else if (draft.order.length - hiddenKeys.size > 1) {
        hiddenKeys.add(columnKey);
      } else {
        toast.info("At least one column must remain visible");
      }
      return { ...draft, hiddenKeys: Array.from(hiddenKeys) };
    });
  };

  /**
   * 将草稿中的列移动一个位置，供键盘和按钮操作使用。
   * @param {string} columnKey 列标识
   * @param {-1|1} direction 移动方向
   */
  const moveDraftColumn = (columnKey, direction) => {
    setColumnDraft((draft) => {
      if (!draft) return draft;
      const order = [...draft.order];
      const currentIndex = order.indexOf(columnKey);
      const targetIndex = currentIndex + direction;
      if (currentIndex < 0 || targetIndex < 0 || targetIndex >= order.length) return draft;
      [order[currentIndex], order[targetIndex]] = [order[targetIndex], order[currentIndex]];
      return { ...draft, order };
    });
  };

  /**
   * 将拖动列放到目标列之前。
   * @param {string} targetKey 目标列标识
   */
  const dropDraftColumn = (targetKey) => {
    if (!draggedColumnKey || draggedColumnKey === targetKey) return;
    setColumnDraft((draft) => {
      if (!draft) return draft;
      const order = draft.order.filter((key) => key !== draggedColumnKey);
      const targetIndex = order.indexOf(targetKey);
      order.splice(targetIndex < 0 ? order.length : targetIndex, 0, draggedColumnKey);
      return { ...draft, order };
    });
    setDraggedColumnKey("");
  };

  /**
   * 保存当前编辑后应用并持久化自定义列配置。
   */
  const applyColumnSettings = async () => {
    if (!columnDraft) return;
    const saved = await saveSheetEdits({ silent: true });
    if (!saved) return;
    saveBomColumnPreferences(columnDraft);
    setColumnPreferences(columnDraft);
    setColumnDraft(null);
    toast.success("Column settings saved");
  };

  /**
   * 将列设置草稿恢复为项目默认值。
   */
  const resetColumnSettings = () => {
    setColumnDraft({
      order: BOM_SHEET_COLUMNS.map((column) => column.key),
      hiddenKeys: [],
    });
  };

  /**
   * 在当前 BOM 云表末尾新增一条可编辑物料行。
   */
  const handleCreateItem = async () => {
    if (!bomOrder?._id) return;

    try {
      const saved = await saveSheetEdits({ silent: true });
      if (!saved) return;
      const response = await createBomItem({
        bomId: bomOrder._id,
        sessionId,
        normalized: { unitQty: 1, productionSurplusQty: 0 },
      }).unwrap();
      setActiveStatuses((statuses) => new Set([...statuses, "invalid"]));
      setOnlySelected(false);
      refreshBomOrder(response?.bomOrder || null);
      toast.success("Item added. Enter a part number or C-number");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to add item");
    }
  };

  /**
   * 删除 Univer 当前选区覆盖的 BOM 物料行。
   */
  const handleDeleteSelectedRows = async () => {
    if (!bomOrder?._id || !univerApiRef.current) return;

    const itemIds = getSelectedBomItemIds(univerApiRef.current, filteredItems);
    if (itemIds.length === 0) {
      toast.info("Select the item rows to delete in the spreadsheet first");
      return;
    }
    if (!window.confirm(`Delete the ${itemIds.length} selected item row(s)?`)) return;

    try {
      const saved = await saveSheetEdits({ silent: true });
      if (!saved) return;
      const response = await deleteBomItems({
        bomId: bomOrder._id,
        sessionId,
        itemIds,
      }).unwrap();
      refreshBomOrder(response?.bomOrder || null);
      toast.success(`${response?.deletedCount || itemIds.length} item row(s) deleted`);
    } catch (error) {
      toast.error(error?.data?.message || "Failed to delete items");
    }
  };

  /**
   * 重新匹配当前 BOM 任务。
   */
  const handleRematch = async () => {
    if (!bomOrder?._id) return;

    try {
      const saved = await saveSheetEdits({ silent: true });
      if (!saved) return;
      const response = await rematchBom({ bomId: bomOrder._id, sessionId }).unwrap();
      refreshBomOrder(response?.bomOrder || null);
      toast.success("BOM rematched");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to rematch BOM");
    }
  };

  /**
   * 使用 AI 为当前选区中的无法匹配 BOM 行生成待确认候选。
   */
  const handleAiRematch = async () => {
    if (!bomOrder?._id || !univerApiRef.current || Number(summary.unmatchedRows || 0) === 0) return;

    const unmatchedItemIds = new Set(
      (bomOrder.items || [])
        .filter((item) => item.matchStatus === "unmatched")
        .map((item) => String(item._id)),
    );
    const itemIds = getSelectedBomItemIds(univerApiRef.current, filteredItems)
      .filter((itemId) => unmatchedItemIds.has(String(itemId)));

    if (itemIds.length === 0) {
      toast.info("Select the unmatched item rows to AI match in the spreadsheet first");
      return;
    }
    if (itemIds.length > MAX_AI_REMATCH_ROWS) {
      toast.info(`Select no more than ${MAX_AI_REMATCH_ROWS} unmatched rows at a time`);
      return;
    }

    try {
      const saved = await saveSheetEdits({ silent: true });
      if (!saved) return;
      const response = await aiRematchBom({ bomId: bomOrder._id, sessionId, itemIds }).unwrap();
      refreshBomOrder(response?.bomOrder || null);
      const pendingCount = Number(response?.ai?.pendingConfirmationRows || 0);
      toast.success(
        pendingCount > 0
          ? `AI found ${pendingCount} local candidate(s). Please review them manually`
          : "AI analysis completed, but no candidates were found in the local product catalog",
      );
    } catch (error) {
      toast.error(error?.data?.message || "AI matching failed");
    }
  };

  /**
   * 接受或拒绝单条 AI 补配候选。
   * @param {string} itemId BOM 明细 ID
   * @param {"accept"|"reject"} decision 审核决定
   */
  const handleAiReview = async (itemId, decision) => {
    if (!bomOrder?._id || !itemId) return;

    try {
      const response = await reviewBomAiSuggestion({
        bomId: bomOrder._id,
        itemId,
        sessionId,
        decision,
      }).unwrap();
      refreshBomOrder(response?.bomOrder || null);
      toast.success(decision === "accept" ? "AI match confirmed" : "AI match rejected");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to review AI match");
    }
  };

  /**
   * 将已选 BOM 明细批量加入现有购物车。
   */
  const addSelectedToCart = async () => {
    if (!bomOrder?._id || selectedItemIds.size === 0) return;

    try {
      const response = await buildBomCartItems({
        bomId: bomOrder._id,
        sessionId,
        itemIds: Array.from(selectedItemIds),
      }).unwrap();

      (response?.cartItems || []).forEach((cartItem) => dispatch(setCartItem(cartItem)));
      toast.success(`${response?.cartItems?.length || 0} BOM item(s) added to cart`);
      navigate("/cart");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to add items to cart");
    }
  };

  const visibleSelectableItems = filteredItems.filter(canSelectBomItem);
  const allVisibleSelected = visibleSelectableItems.length > 0
    && visibleSelectableItems.every((item) => selectedItemIds.has(item._id));

  return (
    <main className={`bom-order-page ${bomOrder ? "is-sheet-mode" : ""}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xls,.xlsx,.csv"
        className="bom-hidden-file"
        onChange={handleFileChange}
        disabled={isUploading}
      />

      {!bomOrder ? (
        <div className="bom-upload-panel">
          <div className="bom-upload-header">
            <div className="bom-upload-icon">X</div>
            <div>
              <h1>BOM Smart Spreadsheet</h1>
              <p>Upload a BOM to generate an editable online spreadsheet, review matches, and add items to your cart.</p>
            </div>
          </div>
          <div className="bom-feature-grid">
            <div><strong>Fast Intelligent Matching</strong><span>Prioritizes matching by C-number and model</span></div>
            <div><strong>Inventory &amp; Pricing Details</strong><span>View stock, tiered pricing, and subtotals</span></div>
            <div><strong>Excel-like Editing</strong><span>Edit the generated spreadsheet online</span></div>
            <div><strong>Add to Cart After Confirmation</strong><span>Uses the existing shopping cart workflow</span></div>
          </div>
          <div className="bom-upload-actions">
            <button type="button" className="bom-primary-button" onClick={openFilePicker} disabled={isUploading}>
              {isUploading ? "Uploading..." : "Upload BOM Now"}
            </button>
            <a className="bom-template-link" href={getBomTemplateUrl()} download>
              Download BOM Template
            </a>
          </div>
          <p className="bom-upload-help">Supports xls, xlsx, and csv; maximum file size: 10 MB</p>
        </div>
      ) : (
        <div className="bom-sheet-workspace">
          <header className="bom-sheet-topbar">
            <div className="bom-sheet-left-actions">
              <button type="button" className="bom-icon-button" title="Back to Upload" onClick={() => setBomOrder(null)}>
                □
              </button>
              <button type="button" className="bom-icon-button" title="Upload New BOM" onClick={openFilePicker}>
                +
              </button>
            </div>
            <h1>{bomOrder.originalFileName || "BOM Online Sheet"}</h1>
            <div className="bom-sheet-stats">
              <div className="bom-match-card">
                <span className="bom-match-ring" style={{ "--match-rate": `${matchRate}%` }} />
                <strong>{matchRate}%</strong>
                <small>Exact Match Rate</small>
              </div>
              <label className="bom-purchase-sets">
                <span>Purchase Qty</span>
                <input
                  type="number"
                  min="1"
                  value={purchaseSets}
                  onChange={(event) => setPurchaseSets(event.target.value)}
                />
                <em>sets</em>
              </label>
            </div>
          </header>

          <section className="bom-sheet-menu">
            <nav>
              <button type="button">Home</button>
              <button type="button">View</button>
              <button type="button" className="is-active">Filter</button>
            </nav>
            <div className="bom-sheet-actions">
              <button type="button" onClick={handleCreateItem} disabled={isCreatingItem || isDeletingItems}>
                {isCreatingItem ? "Adding..." : "Add Item"}
              </button>
              <button type="button" onClick={handleDeleteSelectedRows} disabled={isCreatingItem || isDeletingItems}>
                {isDeletingItems ? "Deleting..." : "Delete Selected Rows"}
              </button>
              <button type="button" onClick={applyPurchaseSets}>Apply Sets</button>
              <button type="button" onClick={() => saveSheetEdits()}>Save Changes</button>
              <button type="button" onClick={handleRematch} disabled={isRematching}>
                {isRematching ? "Matching..." : "Rematch"}
              </button>
              <button
                type="button"
                className="bom-ai-button"
                onClick={handleAiRematch}
                disabled={isAiRematching || Number(summary.unmatchedRows || 0) === 0}
                title="Select up to 20 unmatched rows in the spreadsheet; manual confirmation is still required"
              >
                {isAiRematching ? "AI Matching..." : "AI Match Selected Rows"}
              </button>
              <button type="button" onClick={() => toast.info("TODO: Add quote export support")}>Export Quote</button>
              <button type="button" onClick={openColumnSettings}>Customize Columns</button>
            </div>
          </section>

          {columnDraft && (
            <section className="bom-column-settings" aria-label="Customize column settings">
              <div className="bom-column-settings-heading">
                <div>
                  <strong>Customize Columns</strong>
                  <span>Select columns to display, then drag or use the arrows to reorder them. Changes are saved in this browser.</span>
                </div>
                <div className="bom-column-settings-actions">
                  <button type="button" onClick={resetColumnSettings}>Restore Defaults</button>
                  <button type="button" onClick={() => setColumnDraft(null)}>Cancel</button>
                  <button type="button" className="is-primary" onClick={applyColumnSettings}>Apply</button>
                </div>
              </div>
              <div className="bom-column-settings-list">
                {columnDraft.order.map((columnKey, columnIndex) => {
                  const column = BOM_SHEET_COLUMNS.find((item) => item.key === columnKey);
                  if (!column) return null;
                  const visible = !columnDraft.hiddenKeys.includes(columnKey);
                  return (
                    <div
                      key={columnKey}
                      className={`bom-column-setting-item ${draggedColumnKey === columnKey ? "is-dragging" : ""}`}
                      draggable
                      onDragStart={(event) => {
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", columnKey);
                        setDraggedColumnKey(columnKey);
                      }}
                      onDragEnd={() => setDraggedColumnKey("")}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => dropDraftColumn(columnKey)}
                    >
                      <span className="bom-column-drag-handle" title="Drag to reorder">⋮⋮</span>
                      <label>
                        <input
                          type="checkbox"
                          checked={visible}
                          onChange={() => toggleDraftColumn(columnKey)}
                        />
                        <strong>{column.title}</strong>
                      </label>
                      <small>{column.editable ? "Editable" : "Read-only"}</small>
                      <div className="bom-column-order-buttons">
                        <button
                          type="button"
                          title="Move Up"
                          disabled={columnIndex === 0}
                          onClick={() => moveDraftColumn(columnKey, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          title="Move Down"
                          disabled={columnIndex === columnDraft.order.length - 1}
                          onClick={() => moveDraftColumn(columnKey, 1)}
                        >
                          ↓
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section className="bom-sheet-filterbar">
            <div className="bom-filter-tool">
              <strong>A-Z</strong>
              <span>Global Filter</span>
            </div>
            <div className="bom-filter-tool">
              <strong>⊙</strong>
              <span>Match Result Filter</span>
            </div>
            <label>
              <input
                type="checkbox"
                checked={activeStatuses.size === MATCH_STATUS.length}
                onChange={(event) => toggleAllStatus(event.target.checked)}
              />
              Select All
            </label>
            {MATCH_STATUS.map((status) => (
              <label key={status}>
                <input
                  type="checkbox"
                  checked={activeStatuses.has(status)}
                  onChange={() => toggleStatus(status)}
                />
                {STATUS_LABELS[status]}
              </label>
            ))}
          </section>

          {aiPendingItems.length > 0 && (
            <section className="bom-ai-review-panel" aria-label="AI matches awaiting confirmation">
              <div className="bom-ai-review-heading">
                <div>
                  <strong>AI Matches Awaiting Confirmation</strong>
                  <span>AI results are suggestions only. Verify the part number, brand, package, and parameters before confirming.</span>
                </div>
                <em>{aiPendingItems.length} item(s)</em>
              </div>
              <div className="bom-ai-review-list">
                {aiPendingItems.map((item) => {
                  const suggestion = item.aiSuggestion || {};
                  const product = item.productSnapshot || {};
                  return (
                    <article key={item._id} className="bom-ai-review-item">
                      <div className="bom-ai-review-copy">
                        <span>Row {item.lineNo} · AI confidence {item.confidence || 0}%</span>
                        <strong>{product.Manufacturer_Part_Number || suggestion.suggestedMpn || "-"}</strong>
                        <small>
                          {product.Manufacturer || suggestion.suggestedManufacturer || "Unknown Brand"}
                          {suggestion.suggestedPackage ? ` · ${suggestion.suggestedPackage}` : ""}
                        </small>
                        <p>{suggestion.reason || item.matchReason}</p>
                      </div>
                      <div className="bom-ai-review-actions">
                        <button
                          type="button"
                          className="bom-ai-reject-button"
                          disabled={isReviewingAi}
                          onClick={() => handleAiReview(item._id, "reject")}
                        >
                          Reject
                        </button>
                        <button
                          type="button"
                          className="bom-ai-confirm-button"
                          disabled={isReviewingAi}
                          onClick={() => handleAiReview(item._id, "accept")}
                        >
                          Confirm Match
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          )}

          <section className="bom-univer-wrap">
            <UniverBomSheet
              bomOrder={bomOrder}
              items={filteredItems}
              columns={visibleColumns}
              onReady={handleUniverReady}
            />
          </section>

          <footer className="bom-sheet-footer">
            <div className="bom-footer-left">
              <button
                type="button"
                className="bom-trash-button"
                title="Delete item rows in the current spreadsheet selection"
                onClick={handleDeleteSelectedRows}
                disabled={isDeletingItems}
              >
                ×
              </button>
              <label>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={(event) => toggleVisibleSelection(event.target.checked)}
                />
                Select All
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={onlySelected}
                  onChange={(event) => setOnlySelected(event.target.checked)}
                />
                Selected Only
              </label>
              <span>{selectedItemIds.size} row(s) selected</span>
              <span>{summary.totalRows || 0} item(s) total</span>
            </div>
            <div className="bom-footer-summary">
              <span>{summary.totalRows || 0} row(s) total</span>
              <span>{summary.matchedRows || 0} exact match(es)</span>
              <span>{summary.pendingRows || 0} pending</span>
              <span>{summary.unmatchedRows || 0} unmatched</span>
            </div>
            <div className="bom-footer-actions">
              <div>
                <span>Total:</span>
                <strong>${formatMoney(summary.totalAmount)}</strong>
              </div>
              <button type="button" className="bom-primary-button" onClick={addSelectedToCart} disabled={isAdding || selectedItemIds.size === 0}>
                {isAdding ? "Adding..." : "Add to Cart"}
              </button>
            </div>
          </footer>
        </div>
      )}
    </main>
  );
};

export default BomOrderPage;
